import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Modal } from "./AdminShared";
import RichTextEditor from "@/components/RichTextEditor";

function PageEditorModal({ page, onClose, onSaved }) {
  const [form, setForm] = useState({ ...page });
  const [saving, setSaving] = useState(false);
  const isNew = !page.id;

  const save = async () => {
    if (!form.title || !(form.slug || form.title)) { toast.error("Title and slug required."); return; }
    setSaving(true);
    try {
      if (isNew) await http.post("/admin/cms/pages", form);
      else await http.put(`/admin/cms/pages/${page.id}`, form);
      toast.success("Saved.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <Modal title={isNew ? "New Page" : `Edit · ${page.title}`} onClose={onClose} wide>
      <div className="space-y-4" data-testid="page-editor-modal">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Title</div>
            <input className="stc-input" value={form.title || ""} onChange={e => setForm({...form, title: e.target.value})} data-testid="page-edit-title"/>
          </label>
          <label className="block">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Slug (auto from title if blank)</div>
            <input className="stc-input font-mono" value={form.slug || ""} onChange={e => setForm({...form, slug: e.target.value})} placeholder="member-benefits" data-testid="page-edit-slug"/>
          </label>
        </div>
        <label className="block">
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Subtitle (optional, gold script)</div>
          <input className="stc-input" value={form.subtitle || ""} onChange={e => setForm({...form, subtitle: e.target.value})} data-testid="page-edit-subtitle"/>
        </label>
        <label className="block">
          <div className="flex items-center justify-between mb-1">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Body — full WYSIWYG. Drop images right into the text.</div>
            <a href={form.slug ? `/p/${form.slug}` : "#"} target="_blank" rel="noreferrer" className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] hover:underline">Preview ↗</a>
          </div>
          <RichTextEditor
            value={form.body_markdown || ""}
            onChange={(html) => setForm({...form, body_markdown: html})}
            placeholder="Write something honest. Add images by clicking the image icon in the toolbar."
            testid="page-edit-body"
          />
        </label>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">Status</div>
            <select className="stc-input" value={form.status || "published"} onChange={e => setForm({...form, status: e.target.value})} data-testid="page-edit-status">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </label>
          <label className="block">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">SEO Title</div>
            <input className="stc-input" value={form.seo_title || ""} onChange={e => setForm({...form, seo_title: e.target.value})}/>
          </label>
          <label className="block">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1">SEO Description</div>
            <input className="stc-input" value={form.seo_description || ""} onChange={e => setForm({...form, seo_description: e.target.value})}/>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
          <button onClick={onClose} className="btn-outline text-xs">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-gold text-xs" data-testid="page-edit-save">{saving ? "Saving…" : "Save Page"}</button>
        </div>
      </div>
    </Modal>
  );
}

export default function PagesPanel() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await http.get("/admin/cms/pages"); setPages(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const newPage = () => setEditing({
    slug: "", title: "", subtitle: "", body_markdown: "## Write your content here\n\nMarkdown is supported: **bold**, *italic*, [links](/contact), `code`, lists, > blockquotes.",
    status: "published", footer_column: "", show_in_footer: false, sort_order: 0,
  });

  const seedDefaults = async () => {
    if (!confirm("Seed default pages (Member Benefits, Gift Cards, Our Story, Privacy, Terms, Refund)? Existing pages with the same slug are untouched.")) return;
    setSeeding(true);
    try {
      const { data } = await http.post("/admin/cms/seed-defaults", {});
      toast.success(`Seeded ${data.created} new pages.`);
      load();
    } finally { setSeeding(false); }
  };

  const remove = async (p) => {
    if (!confirm(`Delete page "${p.title}"? This can't be undone.`)) return;
    await http.delete(`/admin/cms/pages/${p.id}`);
    toast.success("Page deleted.");
    load();
  };

  return (
    <div className="space-y-6" data-testid="pages-panel">
      <div className="flex items-center justify-between border border-white/10 bg-[#0a0a0a] p-5">
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-1">CMS Pages</div>
          <p className="font-body text-[12px] text-[#8a857d]">Edit existing pages or create new ones. Live at <code className="text-[#D4B07B]">/p/&lt;slug&gt;</code>. Point footer links to these slugs to control everything from one place.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={seedDefaults} disabled={seeding} className="btn-outline text-xs" data-testid="pages-seed-defaults">{seeding ? "Seeding…" : "Seed Defaults"}</button>
          <button onClick={newPage} className="btn-gold text-xs" data-testid="pages-new-btn"><Plus size={13}/> New Page</button>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a]" data-testid="pages-list">
        {loading ? <div className="p-6 text-[#8a857d]">Loading…</div> :
          pages.length === 0 ? <div className="p-6 text-[#8a857d]">No pages yet. Hit "Seed Defaults" or "New Page" above.</div> :
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Title</th>
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Slug</th>
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Status</th>
                <th className="px-3 py-2.5 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Updated</th>
                <th className="px-3 py-2.5 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {pages.map(p => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`page-row-${p.slug}`}>
                  <td className="px-3 py-2 text-[#F4F1EA] cursor-pointer" onClick={() => setEditing(p)}>{p.title}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[#C9A055]"><a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="hover:underline">/p/{p.slug}</a></td>
                  <td className="px-3 py-2"><span className={`font-ui text-[9px] tracking-[0.22em] uppercase ${p.status === "published" ? "text-emerald-400" : "text-amber-400"}`}>{p.status}</span></td>
                  <td className="px-3 py-2 text-[#B5B0A7] text-xs">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <button onClick={() => setEditing(p)} className="text-[#C9A055] hover:text-[#D4B07B]" data-testid={`page-edit-${p.slug}`}>Edit</button>
                    <button onClick={() => remove(p)} className="text-red-400 hover:text-red-300" data-testid={`page-delete-${p.slug}`}><Trash2 size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>

      {editing && (
        <PageEditorModal page={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
      )}
    </div>
  );
}
